import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { FC, useMemo } from 'react';
import Trans from 'next-translate/Trans';

import ColdPack from '@/components/common/ColdPack';
import pantryIcon from '@/components/pantry/PantryBtn/images/pantry_v5.png';
import { StatusEnum } from '@/types/cart';
import { wrapCurrency } from '@/utils/tool';
import SoldBy from '@/components/common/ProductCard/soldBy';
import { ProductCardProps } from './';
import styles from './cardBody.module.scss';
import Tags from './tags';

const CardBody: FC<Partial<ProductCardProps>> = props => {
  const {
    data,
    showBasePrice = true,
    showTags = true,
    bottomTip,
    fixedTitletHeight = false,
    isLightningCard,
    isHorizontal,
    priceCls,
    showSoldBy = true,
    showExtraTag
  } = props;

  const { t } = useTranslation('common');

  const isSmallCard = useMemo(() => {
    if (props.cardSize === 'sm') return true;
    return false;
  }, [props.cardSize]);

  return (
    <div className={classNames(styles.midBox, { [styles.horizontal]: isHorizontal })} data-role="mid-box">
      <div className={styles.topTag}>
        {data?.is_hotdish === 'Y' && <div className={styles.topTagHotdish}>{t('card_hotdish_tag')}</div>}
        {data?.category === 'bundle' && <div className={styles.topTagBundle}>{t('card_bundle_tag')}</div>}
      </div>
      {/* {showBrandName && data?.brand_name && <div className={styles.brandName}>{data?.brand_name}</div>} */}
      {!!data?.sponsored_text && (
        <div data-testid="wid-product-card-sponsored-text" className={styles.brandName}>
          {data.sponsored_text}
        </div>
      )}
      {!data?.sponsored_text && !!data?.brand_name && (
        <div
          data-testid="wid-product-card-brand"
          className="text-surface-100-fg-minor enki-body-2xs-medium line-clamp-1">
          {data?.brand_name}
        </div>
      )}
      <div
        data-role="product-title"
        className={classNames('enki-body-3xs text-surface-100-fg-default !leading-[20px]', styles.title, {
          [styles.fixedHeight]: fixedTitletHeight
        })}>
        {data?.is_pantry && <img className={styles.pantryIcon} src={pantryIcon} alt="pantry icon" />}
        {data?.biz_type !== 'seller' && !data?.is_pantry && !!data?.is_colding_package && (
          <div className={styles.coldPackTag}>
            <ColdPack />
          </div>
        )}
        {data?.name}
      </div>

      {data?.sold_status === StatusEnum?.sold_out && data?.restock_tip && (
        <div className={styles.restockTip}>{data?.restock_tip}</div>
      )}

      {!!showTags && !!isHorizontal && <Tags data={data} {...props} />}

      <div className={classNames(styles.price, priceCls)}>
        {!isHorizontal && (
          <div
            className={classNames('enki-body-base-strong text-pricing-txt', styles.productPrice, {
              'leading-5 text-[17px]': isSmallCard
            })}>
            {isLightningCard
              ? data?.special_price && `$${wrapCurrency(data?.special_price)}`
              : data?.price && `$${wrapCurrency(data?.price)}`}
          </div>
        )}
        {isLightningCard
          ? !!data?.base_price &&
            data?.base_price >= data?.special_price && (
              <div className={classNames('enki-body-sm-strong text-surface-100-fg-minor', styles.basePrice)}>
                ${wrapCurrency(data?.base_price)}
              </div>
            )
          : !isHorizontal &&
            !!showBasePrice &&
            !!data?.base_price &&
            data?.base_price > data?.price && (
              <div className={classNames('enki-body-sm-strong text-surface-100-fg-minor', styles.basePrice)}>
                ${wrapCurrency(data?.base_price)}
              </div>
            )}
      </div>
      {!!isHorizontal && (
        <div>
          {!!data?.price && (
            <span
              className={classNames('enki-body-base-strong text-pricing-txt', styles.productPrice, {
                'leading-5 text-[17px]': isSmallCard
              })}>
              {`$${wrapCurrency(data?.price)}`}
            </span>
          )}

          {!!showBasePrice && !!data?.base_price && data?.base_price > data?.price && (
            <span className={classNames(styles.basePrice, 'text-surface-100-fg-minor enki-body-2xs-strong')}>
              ${wrapCurrency(data?.base_price)}
            </span>
          )}

          {showExtraTag && !!+data?.discount_percentage && (
            <div>
              <span className="enki-body-3xs-strong rounded bg-[#ffe2bb] inline-flex items-center tracking-[-0.2px] text-center text-[#eb5a03] text-[10px] mt-0.5 px-1 py-[2.5px]">
                <Trans
                  i18nKey="common:order_share_grocery_extra_tag"
                  components={{
                    span: <span className={styles.title} />
                  }}
                  values={{
                    discount: data?.discount_percentage
                  }}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {!!showTags && !isHorizontal && <Tags data={data} {...props} />}
      {showSoldBy && data?.biz_type === 'seller' ? <SoldBy isHorizontal={isHorizontal} data={data} /> : null}
      {!isLightningCard && bottomTip && <div className={styles.bottomTip}>{bottomTip}</div>}
    </div>
  );
};
export default CardBody;
